import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { Row, Col, Card, CardBody, Button, Spinner, Alert } from "reactstrap"
import { RootState } from "store"
import { isValidOTP } from "utils/helpers"

interface VerifyOTPProps {
    username: string,
    submitButtonText: string,
    resendOTP: () => Promise<boolean>,
    changeEmail: () => void,
    handleVerification: (otp: string) => void
}

const VerifyOTP: React.FC<VerifyOTPProps> = (
    {
        username,
        submitButtonText,
        resendOTP,
        changeEmail,
        handleVerification
    }) => {

    const { loading, error } = useSelector((state: RootState) => state.auth)
    const [resendIn, setResendIn] = useState<number>(0);
    const [resending, setResending] = useState<boolean>(false);

    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']); // 6-digit OTP


    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (resendIn > 0) {
            interval = setInterval(() => {
                setResendIn((prev) => prev - 1);
            }, 1000)
        } else {
            if (interval) {
                clearInterval(interval);
            }
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [resendIn])

    useEffect(() => {
        setResendIn(30);
    }, [])

    const handleResendOTP = async () => {
        if (resendIn || resending) return;
        try {
            setResending(true);
            const success = await resendOTP();
            if (success) {
                setResendIn(30);
                toast.success("OTP has been resent!");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setResending(false);
        }
    }


    const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const previousInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
            previousInput?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = e.clipboardData.getData('Text').trim();
        if (pastedData.length === otp.length && /^[0-9]+$/.test(pastedData)) {
            const newOtp = pastedData.split('');
            setOtp(newOtp);

            // Focus the last input field
            const lastInput = document.getElementById(`otp-${otp.length - 1}`) as HTMLInputElement;
            lastInput?.focus();
        }
        e.preventDefault(); // Prevent the default paste behavior
    };


    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;
        const newOtp = [...otp];
        newOtp[index] = value;

        // Move focus to next input when a digit is entered
        if (value && index < otp.length - 1) {
            const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
            nextInput?.focus();
        }

        setOtp(newOtp);
    };


    useEffect(() => {
        if (isValidOTP(otp))
            handleVerification(otp.join(''));
    }, [otp])

    return (
        <React.Fragment>
            <h2 className='auth-title'>Verify Your Email</h2>
            <span className="auth-helper-text">Please enter the 6 digit code sent to <span className="fw-semibold d-block">{username} <span onClick={changeEmail} className='text-primary cursor-pointer'>Edit Email</span></span></span>
            {error && error ? (<Alert className="mt-4" color="danger"> {error} </Alert>) : null}
            <form className="mt-4">
                <Row>
                    {
                        otp.map((digit, index) => {
                            return (
                                <Col className="col-2" key={index}>
                                    <div className="mb-3">
                                        <label htmlFor="digit1-input" className="visually-hidden">Digit {index + 1}</label>
                                        <input
                                            className="form-control digit text-center auth-input"
                                            autoComplete='off'
                                            key={index}
                                            id={`otp-${index}`}
                                            type="tel"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(e, index)}
                                            onKeyDown={(e) => handleBackspace(e, index)}
                                            onPaste={index === 0 ? handlePaste : undefined} // Only listen to paste on the first input
                                        />
                                    </div>
                                </Col>
                            )
                        })
                    }
                </Row>
            </form>
            <div className="mt-3">
                <Button onClick={() => {
                    if (isValidOTP(otp))
                        handleVerification(otp.join(''))
                }}
                    disabled={loading} className="theme-btn theme-btn-lg btn-primary w-100">
                    {loading && <Spinner size="sm" className='me-2'> Siging in... </Spinner>}
                    {submitButtonText}
                </Button>
            </div>
            <div className="mt-4 text-center">
                <p className="mb-0">Didn't receive a code ? <span className={`${(resendIn || resending) ? 'text-muted' : 'text-primary cursor-pointer'}`} style={{ padding: '2px !important' }} onClick={handleResendOTP}>{resendIn ? `Resend in ${resendIn} seconds` : `${resendIn ? 'Sending...' : 'Resend OTP'}`}</span> </p>
            </div>
        </React.Fragment>
    )

}

export default VerifyOTP